import { z } from "zod";

/** Mirrors backend `PaymentStatusV4` on order payments. */
export enum PaymentStatusV4 {
  PENDING = "PENDING",
  PARTIALLY_COLLECTED = "PARTIALLY_COLLECTED",
  COLLECTED = "COLLECTED",
  VERIFIED = "VERIFIED",
  FAILED = "FAILED",
  CANCELLED="CANCELLED"
}

export enum PaymentMethodType {
  CARD = "CARD",
  UPI = "UPI",
  NET_BANKING = "NET_BANKING",
  COD = "COD",
  WALLET = "WALLET",
  EXTERNAL = "EXTERNAL",
  UNKNOWN = "UNKNOWN",
}

export enum PaymentProvider {
  JUSPAY = "JUSPAY",
  NONE = "NONE",
}

/** Mirrors backend `RefundStatus` on payment refunds. */
export enum RefundStatus {
  PENDING = "PENDING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
  MANUAL_REVIEW = "MANUAL_REVIEW",
}

/** Individual refund recorded against a payment. `amount` is in rupees. */
export const orderPaymentRefundSchema = z.object({
  status: z.nativeEnum(RefundStatus),
  amount: z.number(),
  initiatedAt: z.number().nullish(),
  gatewayRefundId: z.string().nullish(),
  reason: z.string().nullish(),
  isAutoRefund: z.boolean(),
});

export type OrderPaymentRefundDto = z.infer<typeof orderPaymentRefundSchema>;

const juspayMetadataSchema = z.object({
  inputOrderId: z.string().nullish(),
  orderId: z.string().nullish(),
  customerId: z.string().nullish(),
  paymentLinks: z
    .object({
      web: z.string().nullish(),
      mobile: z.string().nullish(),
      iframe: z.string().nullish(),
    })
    .nullish(),
  merchantId: z.string().nullish(),
  clientAuthToken: z
    .object({
      expiry: z.string().nullish(),
      token: z.string().nullish(),
    })
    .nullish(),
});

export const orderPaymentResponseSchema = z.object({
  id: z.string(),
  userId: z.string(),
  orderId: z.string().nullish(),
  gatewayOrderId: z.string().nullish(),
  amount: z.number(),
  status: z.nativeEnum(PaymentStatusV4),
  gatewayTransactionId: z.string().nullish(),
  gatewayCustomerId: z.string().nullish(),
  provider: z.nativeEnum(PaymentProvider),
  juspayMetadata: juspayMetadataSchema.nullish(),
  method: z.nativeEnum(PaymentMethodType).nullish(),
  notes: z.string().nullish(),
  /** Total refunded against this payment, in rupees. */
  refundedAmount: z.number().nullish(),
  /** Individual refunds recorded against this payment. */
  refunds: z.array(orderPaymentRefundSchema).nullish(),
});

export type OrderPaymentResponseDto = z.infer<typeof orderPaymentResponseSchema>;
